/**
 * One mailbox in the rail: its address as the heading, then the same six rows
 * every mailbox has, then its other server folders under "More folders".
 *
 * The shared mailbox (info@) and a person's own (kristi@) are drawn by this
 * one component on purpose. The muddle the owner described was a rail with one
 * real mailbox, a second one only as a sender, and Sent findable only under
 * "IMAP folders" - two kinds of mailbox would have been two shapes of tree.
 *
 * A heading folds the tree away; folded, it still carries the unread count, as
 * the call rail's folds carry their one line (see `RailFold`). A right-click on
 * any row offers it to Favorites. The heading can be dragged onto another tree to
 * put the mailboxes in another order (`useMailboxDrag`).
 */
import { AlertTriangle, ChevronDown, GripVertical, Settings2 } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import type { MailboxView } from "../../../../lib/mail";
import { ContextMenu, useContextMenu } from "../../../shell/ContextMenu";
import { ImapFolders } from "../imap";
import { ROW_ICON, rowLabel } from "./labels";
import type { MailRail } from "./useMailRail";
import { BOX_ROWS, rowView, sameView, viewRef, type MailView } from "./view";
import { PinItem } from "./PinItem";
import type { MailboxDrag } from "./useMailboxDrag";

export function MailboxTree({
  box,
  view,
  onView,
  rail,
  open,
  onToggle,
  onSettings,
  onToast,
  drag,
}: {
  box: MailboxView;
  view: MailView;
  onView: (view: MailView) => void;
  rail: MailRail;
  open: boolean;
  onToggle: () => void;
  /** Own mailboxes only: the dialog that changes or removes it. */
  onSettings?: () => void;
  onToast: (msg: string) => void;
  /** Set when there is more than one mailbox to put in order. */
  drag?: MailboxDrag;
}) {
  const t = useT();
  const menu = useContextMenu<string>();
  const sent = rail.sentOf(box.id);
  // Sent is a server folder. With no IMAP login there is no folder to read,
  // without the mail right this person may not open it, and once the tree has
  // answered without one there is none to open.
  const sentMissing = !box.imap || !box.imapOpen || (rail.foldersKnown(box.id) && !sent);
  const sentWhy = !box.imap ? "mail.box.noImap" : !box.imapOpen ? "mail.box.noRight" : "mail.box.noSent";

  return (
    <section
      className={cn("crm-acc crm-mbox", open && "is-open", drag?.carried && "is-carried", drag?.line && `drop-${drag.line}`)}
      {...drag?.target}
    >
      <div className="crm-mbox-h" {...drag?.handle}>
        {drag ? <GripVertical className="crm-mbox-grip size-3.5" aria-hidden /> : null}
        <button
          type="button"
          className="crm-acc-h"
          aria-expanded={open}
          onClick={onToggle}
          onKeyDown={drag?.onKeyDown}
          title={drag ? `${box.address}\n${t("mail.box.dragHint")}` : box.address}
        >
          <span className="crm-mbox-t">{box.address}</span>
          {!open && box.unread ? <span className="crm-n crm-n-hot">{box.unread}</span> : null}
          <ChevronDown className="crm-acc-chev size-3.5" strokeWidth={2.5} />
        </button>
        {onSettings ? (
          <button
            type="button"
            className="crm-cb crm-cb-sm"
            onClick={onSettings}
            title={t("mail.box.settings")}
            aria-label={t("mail.box.settings")}
          >
            <Settings2 className="size-3.5" />
          </button>
        ) : null}
      </div>

      {open ? (
        <div className="crm-acc-b">
          {box.error ? (
            <p className="flex gap-1.5 px-[14px] pb-1 text-[11.5px] leading-snug text-destructive">
              <AlertTriangle className="mt-px size-3.5 shrink-0" />
              <span>{t("mail.box.error", { reason: box.error })}</span>
            </p>
          ) : null}

          {BOX_ROWS.map((row) => {
            const target = rowView(box.id, row);
            const Icon = ROW_ICON[row];
            const on = sameView(view, target);
            const n = row === "inbox" ? box.unread : row === "drafts" ? box.drafts : row === "scheduled" ? box.scheduled : 0;
            const dead = row === "sent" && sentMissing;
            return (
              <button
                key={row}
                type="button"
                className={cn("crm-fold", on && "is-on", dead && "opacity-50")}
                aria-current={on ? "true" : undefined}
                disabled={dead}
                title={dead ? t(sentWhy) : undefined}
                onClick={() => onView(target)}
                {...menu.rowProps(viewRef(target))}
              >
                <Icon className="size-4" strokeWidth={1.8} />
                <span className="crm-fold-l">{rowLabel(t, row)}</span>
                {n ? <span className={cn("crm-n", row === "inbox" && "crm-n-hot")}>{n}</span> : null}
              </button>
            );
          })}

          {/* The account's other folders. Mounted while the tree is open
              only, so a folded mailbox costs no IMAP login. */}
          {box.imap ? (
            <ImapFolders
              mailbox={box.id}
              canManage={box.imapOpen}
              openPath={view.kind === "folder" && view.mailbox === box.id ? view.path : null}
              onOpen={(path) => onView({ kind: "folder", mailbox: box.id, path })}
              onClose={() => onView(rowView(box.id, "inbox"))}
              onToast={onToast}
              onFolders={(list) => rail.setFolders(box.id, list)}
              pinned={(path) => rail.isPinned(`${box.id}:folder:${path}`)}
              onPin={(path) => rail.togglePin(`${box.id}:folder:${path}`)}
            />
          ) : null}
        </div>
      ) : null}

      <ContextMenu menu={menu} label={t("mail.fav.title")}>
        {(ref, close) => <PinItem pinned={rail.isPinned(ref)} onToggle={() => rail.togglePin(ref)} close={close} />}
      </ContextMenu>
    </section>
  );
}
